import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, updateDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";

const COLLECTION = "uyelikTalepleri";

type Durum = "bekliyor" | "onaylandi" | "reddedildi";

interface Talep {
  id: string;
  ad: string;
  soyad: string;
  email: string;
  telefon: string;
  ilce: string;
  meslek: string;
  neden: string;
  tarih: Timestamp | null;
  durum: Durum;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  bekliyor: { label: "Bekliyor", color: "bg-yellow-100 text-yellow-700" },
  onaylandi: { label: "Onaylandı", color: "bg-green-100 text-green-700" },
  reddedildi: { label: "Reddedildi", color: "bg-red-100 text-red-700" },
};

// Firestore Timestamp → "GG.AA.YYYY" (null/eksik güvenli).
function formatTarih(t: Timestamp | null): string {
  if (!t || typeof t.toDate !== "function") return "—";
  try {
    return t.toDate().toLocaleDateString("tr-TR");
  } catch {
    return "—";
  }
}

const ApplicationsPage: React.FC = () => {
  const [rows, setRows] = useState<Talep[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<string>("hepsi");
  const [selected, setSelected] = useState<Talep | null>(null);

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      const snap = await getDocs(query(collection(db, COLLECTION), orderBy("tarih", "desc")));
      setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Talep[]);
    } catch (e) {
      console.error("Üyelik talepleri okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((a) => {
    const matchSearch = !search ||
      `${a.ad} ${a.soyad}`.toLowerCase().includes(search.toLowerCase()) ||
      (a.email ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (a.telefon ?? "").includes(search);
    const matchFilter = filter === "hepsi" || a.durum === filter;
    return matchSearch && matchFilter;
  });

  const updateStatus = async (id: string, durum: Durum) => {
    try {
      await updateDoc(doc(db, COLLECTION, id), { durum });
      setRows((prev) => prev.map((a) => (a.id === id ? { ...a, durum } : a)));
      setSelected((prev) => (prev && prev.id === id ? { ...prev, durum } : prev));
    } catch (e) {
      console.error("Durum güncellenemedi:", e);
      window.alert("Durum güncellenemedi, lütfen tekrar deneyin.");
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Bu talep silinsin mi?")) return;
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      setRows((prev) => prev.filter((a) => a.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      console.error("Silinemedi:", e);
      window.alert("Silme başarısız, lütfen tekrar deneyin.");
    }
  };

  const bekleyen = rows.filter((a) => a.durum === "bekliyor").length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Üyelik Talepleri</h2>
          <p className="text-sm text-gray-500 mt-0.5">{bekleyen} bekleyen, {rows.length} toplam talep · mobil "Üye Ol" formundan</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["hepsi", "bekliyor", "onaylandi", "reddedildi"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${filter === f ? "bg-[#8B1428] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}>
              {f === "hepsi" ? "Hepsi" : STATUS_LABELS[f]?.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><i className="fa-solid fa-circle-notch fa-spin text-3xl" /></div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Talepler yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-file-lines text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Henüz üyelik talebi yok</p>
          <p className="text-gray-400 text-sm mt-1">Mobil uygulamadaki "Üye Ol" formu doldurulduğunda burada görünecek</p>
        </div>
      ) : (
        <>
          <div className="relative mb-4">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input type="text" placeholder="Ad, e-posta veya telefon ile ara..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30" />
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ad Soyad</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">E-posta</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Telefon</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Tarih</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Durum</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <button onClick={() => setSelected(a)} className="hover:text-[#8B1428] transition-colors text-left">
                        {a.ad} {a.soyad}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{a.email}</td>
                    <td className="px-4 py-3 text-gray-500 font-mono hidden sm:table-cell">{a.telefon}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">{formatTarih(a.tarih)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_LABELS[a.durum]?.color ?? "bg-gray-100 text-gray-600"}`}>
                        {STATUS_LABELS[a.durum]?.label ?? a.durum}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {a.durum === "bekliyor" && (
                          <>
                            <button onClick={() => updateStatus(a.id, "onaylandi")} className="text-green-600 hover:text-green-800 text-xs font-semibold">Onayla</button>
                            <button onClick={() => updateStatus(a.id, "reddedildi")} className="text-red-500 hover:text-red-700 text-xs font-semibold">Reddet</button>
                          </>
                        )}
                        <button onClick={() => remove(a.id)} className="text-gray-400 hover:text-red-500 transition-colors"><i className="fa-solid fa-trash text-xs" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">{filtered.length} talep gösteriliyor</div>
          </div>
        </>
      )}

      {/* Detay Modal */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setSelected(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative">
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><i className="fa-solid fa-xmark text-lg" /></button>
              <h3 className="text-lg font-bold text-gray-800 mb-1">{selected.ad} {selected.soyad}</h3>
              <p className="text-xs text-gray-400 mb-5">{formatTarih(selected.tarih)}</p>
              <div className="grid grid-cols-2 gap-3 text-sm mb-5">
                {[
                  ["E-posta", selected.email],
                  ["Telefon", selected.telefon],
                  ["İlçe", selected.ilce || "—"],
                  ["Meslek", selected.meslek || "—"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-xs text-gray-400 font-semibold uppercase mb-0.5">{k}</div>
                    <div className="text-gray-700">{v}</div>
                  </div>
                ))}
              </div>
              {selected.neden && (
                <div className="mb-5">
                  <div className="text-xs text-gray-400 font-semibold uppercase mb-1">Katılma Nedeni</div>
                  <p className="text-gray-600 text-sm leading-relaxed bg-gray-50 rounded-lg p-3">{selected.neden}</p>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => updateStatus(selected.id, "onaylandi")} className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">Onayla</button>
                <button onClick={() => updateStatus(selected.id, "reddedildi")} className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">Reddet</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ApplicationsPage;
